import { ENTITLEMENTS } from './entitlements.constants';

export const ROLES = {
  accountAdmin: 'account_admin',
  accountOwner: 'account_owner',
  accountUser: 'account_user',
  opAdmin: 'orderprotection_admin',
  opGlobalSupport: 'orderprotection_support',
  opSupportAdmin: 'orderprotection_support_admin',
  opFinance: 'orderprotection_finance',
  opImplementation: 'orderprotection_implementation',
  opPartner: 'orderprotection_partner',
  opEngineering: 'orderprotection_engineering',
  developer: 'developer',
  // Appended LAST: `2.roles.seed.ts` createMany's the missing roles in `Object.values(ROLES)` order,
  // so on a fresh seed this lands on the next auto-increment id. Must line up with
  // ROLE_ID.accountSupport below (same fragility as `store_collaborator`) — verify on a fresh seed.
  accountSupport: 'account_support',
};

/**
 * OP-global roles: a user with one of these roles operates across ALL stores rather than a scoped
 * subset. Single source of truth for "is this caller store-global", consumed by both
 * StoreAccessService.isGlobalRole (request-time access checks) and merchant-svc store resolution
 * (findUserStores / getAccessStoreIds) so the two enforcement paths never drift.
 *
 * Note: `ROLES.developer` is intentionally excluded — developers are scoped to collaborator stores
 * only, not all stores.
 */
export const GLOBAL_ROLES: readonly string[] = [
  ROLES.opAdmin,
  ROLES.opFinance,
  ROLES.opImplementation,
  ROLES.opPartner,
  ROLES.opGlobalSupport,
  ROLES.opSupportAdmin,
  ROLES.opEngineering,
];

export const isGlobalRole = (role?: string | null): boolean => !!role && GLOBAL_ROLES.includes(role);

export const ROLE_ID = {
  accountAdmin: 1,
  accountOwner: 2,
  accountUser: 3,
  opAdmin: 4,
  opGlobalSupport: 5,
  opSupportAdmin: 6,
  opFinance: 7,
  opImplementation: 8,
  opPartner: 9,
  opEngineering: 10,
  developer: 11,
  accountSupport: 12,
};

export const getRoleName = (roleId: number) => {
  const entry = Object.entries(ROLE_ID).find(([, id]) => {
    return id === roleId;
  });

  if (!entry) {
    return null;
  }

  return ROLES[entry[0]];
};

export const ROLE_IDS_BRAND = [
  ROLE_ID.accountAdmin,
  ROLE_ID.accountOwner,
  ROLE_ID.accountUser,
  ROLE_ID.accountSupport,
];

/**
 * Account roles that grant ACCOUNT-WIDE store access (every current + future store), as opposed to
 * `account_user` which is store-scoped. Single source of truth for the hand-rolled whitelists that
 * `StoreAccessService.canAccessStore` doesn't cover (auth.service `userCanAccessStore`,
 * user.access `searchMentionable`) so a new account-wide role can't silently miss one.
 */
export const ROLE_IDS_ACCOUNT_WIDE = [ROLE_ID.accountOwner, ROLE_ID.accountAdmin, ROLE_ID.accountSupport];

export const ROLE_IDS_OP = [
  ROLE_ID.opAdmin,
  ROLE_ID.opGlobalSupport,
  ROLE_ID.opSupportAdmin,
  ROLE_ID.opFinance,
  ROLE_ID.opImplementation,
  ROLE_ID.opEngineering,
];

/** Who a verified person speaks for on a store's claim. */
export enum StoreSenderRole {
  BRAND = 'brand',
  OP = 'op',
}

export interface StoreSenderUser {
  roleId: number;
  accountId?: string | null;
  deletedAt?: unknown;
  storeRoles?: { storeId: string }[] | null;
}

/** OP roles win over any store role; the brand is a store role on this store or an account-wide role on its account. */
export function storeSenderRole(
  user: StoreSenderUser | null | undefined,
  storeId: string,
  storeAccountId?: string | null,
): StoreSenderRole | null {
  if (!user || user.deletedAt) return null;
  if (ROLE_IDS_OP.includes(user.roleId)) return StoreSenderRole.OP;
  if (ROLE_IDS_ACCOUNT_WIDE.includes(user.roleId) && user.accountId && user.accountId === storeAccountId)
    return StoreSenderRole.BRAND;
  if (user.storeRoles?.some(role => role.storeId === storeId)) return StoreSenderRole.BRAND;
  return null;
}

export const STORE_ROLES = {
  storeAdmin: 'store_admin',
  storeOwner: 'store_owner',
  storeSupport: 'store_support',
  storeUser: 'store_user',
  storeCollaborator: 'store_collaborator',
};

export const STORE_ROLE_ID = {
  storeAdmin: 1,
  storeOwner: 2,
  storeSupport: 3,
  storeUser: 4,
  // ID 5 must match the `store_collaborator` row created by `pnpm seed identity-svc`
  // (prisma/seeds/*/4.store-roles.seed.ts). Verify against a fresh DB before deploying.
  storeCollaborator: 5,
};

const EVERY_ENTITLEMENT = Object.values(ENTITLEMENTS).reduce((acc, entitlement) => {
  return [...acc, ...Object.values(entitlement)];
}, []);

const ADMIN_EXCLUDE_ENTITLEMENT = [
  ...Object.values(ENTITLEMENTS.financeAdmin),
  ...Object.values(ENTITLEMENTS.implementation),
  ...Object.values(ENTITLEMENTS.engineering),
  ...Object.values(ENTITLEMENTS.claimAutomation),
];

const FINANCE_EXCLUDE_ENTITLEMENT = [
  ...Object.values(ENTITLEMENTS.implementation),
  ...Object.values(ENTITLEMENTS.engineering),
  ...Object.values(ENTITLEMENTS.claimAutomation),
];

const AdminEntitlements = EVERY_ENTITLEMENT.filter(entitlement => !ADMIN_EXCLUDE_ENTITLEMENT.includes(entitlement));
const ImplementationEntitlements = [
  ...AdminEntitlements,
  ...Object.values(ENTITLEMENTS.implementation),
  // Read-only impersonation is available to implementation as well as engineering (ENG-5877). Only impersonate is
  // granted, not the rest of ENTITLEMENTS.engineering, which the admin exclude list still strips.
  ENTITLEMENTS.engineering.impersonate,
];
const FinanceAdminEntitlements = EVERY_ENTITLEMENT.filter(
  entitlement => !FINANCE_EXCLUDE_ENTITLEMENT.includes(entitlement),
);

export const ROLE_ENTITLEMENTS_MAP = {
  [ROLES.opEngineering]: EVERY_ENTITLEMENT,
  [ROLES.opAdmin]: AdminEntitlements,
  [ROLES.opImplementation]: ImplementationEntitlements,
  [ROLES.opFinance]: FinanceAdminEntitlements,
  [ROLES.accountOwner]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ...Object.values(ENTITLEMENTS.billingSettings),
    ...Object.values(ENTITLEMENTS.settingsMetadata),
    ENTITLEMENTS.billingMethod.create,
    ENTITLEMENTS.billingMethod.read,
    ENTITLEMENTS.billingMethod.update,
    ENTITLEMENTS.pricing.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.returns.update,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.accountStores.read,
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.customizations.update,
    ENTITLEMENTS.teamMember.changeRole,
    ENTITLEMENTS.teamMember.grantOwnership,
    ENTITLEMENTS.teamMember.invite,
    ENTITLEMENTS.teamMember.edit,
    ENTITLEMENTS.teamMember.remove,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.settingsRevShare.read,
    ENTITLEMENTS.macro.create,
    ENTITLEMENTS.macro.delete,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.macro.update,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.store.update,
    ENTITLEMENTS.user.create,
    ENTITLEMENTS.user.delete,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.contact.update,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.analytics.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.payout.read,
    ENTITLEMENTS.finance.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.account.update,
    ENTITLEMENTS.invoice.read,
    ENTITLEMENTS.paymentMethod.create,
    ENTITLEMENTS.paymentMethod.delete,
    ENTITLEMENTS.paymentMethod.read,
    ENTITLEMENTS.paymentMethod.update,
    ENTITLEMENTS.payoutMethod.create,
    ENTITLEMENTS.payoutMethod.delete,
    ENTITLEMENTS.payoutMethod.read,
    ENTITLEMENTS.payoutMethod.update,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.productVariant.create,
    ENTITLEMENTS.productVariant.delete,
    ENTITLEMENTS.productVariant.read,
    ENTITLEMENTS.productVariant.update,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.webhook.create,
    ENTITLEMENTS.webhook.delete,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.webhook.update,
    ENTITLEMENTS.billing.read,
    ENTITLEMENTS.settingsCustomerCommunication.read,
    ENTITLEMENTS.settingsCustomerCommunication.update,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.update,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.settingsFulfillment.read,
    ENTITLEMENTS.settingsFulfillment.update,
    ENTITLEMENTS.settingsReplacementCost.read,
    ENTITLEMENTS.settingsReplacementCost.update,
    ENTITLEMENTS.settingsBackorderReship.read,
    ENTITLEMENTS.settingsBackorderReship.update,
    ENTITLEMENTS.apiTokens.create,
    ENTITLEMENTS.apiTokens.delete,
    ENTITLEMENTS.apiTokens.read,
    ENTITLEMENTS.apiTokens.update,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.integrations.read,
    ENTITLEMENTS.integrations.update,
    ENTITLEMENTS.abTests.read,
    ENTITLEMENTS.abTests.delete,
    ENTITLEMENTS.abTests.update,
    ENTITLEMENTS.abTests.create,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.sop.delete,
    ENTITLEMENTS.sop.update,
    ENTITLEMENTS.sop.create,
    ENTITLEMENTS.sop.setState,
    ENTITLEMENTS.customerClaimView.read,
    ENTITLEMENTS.customerClaimView.update,
    ENTITLEMENTS.customers.read,
    // Developer capability for an account's OWN stores: build/manage private apps and install them
    // on stores the account owns. Gated at the controller layer by the DEVELOPER_PLATFORM /
    // MARKETPLACE feature flags. See features/developer-accounts (Phase 001).
    ...Object.values(ENTITLEMENTS.developerApp),
    ENTITLEMENTS.developerInstallation.read,
    ENTITLEMENTS.marketplace.read,
    ENTITLEMENTS.marketplace.install,
    ENTITLEMENTS.marketplace.uninstall,
  ],
  [ROLES.accountAdmin]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ...Object.values(ENTITLEMENTS.billingSettings),
    ...Object.values(ENTITLEMENTS.settingsMetadata),
    ENTITLEMENTS.billingMethod.create,
    ENTITLEMENTS.billingMethod.read,
    ENTITLEMENTS.billingMethod.update,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.returns.update,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.accountStores.read,
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.customizations.update,
    ENTITLEMENTS.contact.update,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.teamMember.changeRole,
    ENTITLEMENTS.teamMember.invite,
    ENTITLEMENTS.teamMember.edit,
    ENTITLEMENTS.teamMember.remove,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.settingsRevShare.read,
    ENTITLEMENTS.macro.create,
    ENTITLEMENTS.macro.delete,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.macro.update,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.store.update,
    ENTITLEMENTS.user.create,
    ENTITLEMENTS.user.delete,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.analytics.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.payout.read,
    ENTITLEMENTS.finance.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.account.update,
    ENTITLEMENTS.invoice.read,
    ENTITLEMENTS.paymentMethod.create,
    ENTITLEMENTS.paymentMethod.delete,
    ENTITLEMENTS.paymentMethod.read,
    ENTITLEMENTS.paymentMethod.update,
    ENTITLEMENTS.payoutMethod.create,
    ENTITLEMENTS.payoutMethod.delete,
    ENTITLEMENTS.payoutMethod.read,
    ENTITLEMENTS.payoutMethod.update,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.webhook.create,
    ENTITLEMENTS.webhook.delete,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.webhook.update,
    ENTITLEMENTS.billing.read,
    ENTITLEMENTS.settingsCustomerCommunication.read,
    ENTITLEMENTS.settingsCustomerCommunication.update,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.update,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.settingsFulfillment.read,
    ENTITLEMENTS.settingsFulfillment.update,
    ENTITLEMENTS.settingsReplacementCost.read,
    ENTITLEMENTS.settingsReplacementCost.update,
    ENTITLEMENTS.settingsBackorderReship.read,
    ENTITLEMENTS.settingsBackorderReship.update,
    ENTITLEMENTS.apiTokens.create,
    ENTITLEMENTS.apiTokens.delete,
    ENTITLEMENTS.apiTokens.read,
    ENTITLEMENTS.apiTokens.update,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.integrations.read,
    ENTITLEMENTS.integrations.update,
    ENTITLEMENTS.abTests.read,
    ENTITLEMENTS.abTests.delete,
    ENTITLEMENTS.abTests.update,
    ENTITLEMENTS.abTests.create,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.sop.delete,
    ENTITLEMENTS.sop.update,
    ENTITLEMENTS.sop.create,
    ENTITLEMENTS.sop.setState,
    ENTITLEMENTS.pricing.read,
    ENTITLEMENTS.customerClaimView.read,
    ENTITLEMENTS.customerClaimView.update,
    ENTITLEMENTS.customers.read,
  ],
  [ROLES.accountUser]: [
    ENTITLEMENTS.settingsMetadata.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.billingSettings.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.label.read,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.pricing.read,
  ],
  // Account-wide read-mostly support. Can work claims and orders across every store in the account,
  // but has NO billing/pricing, NO team-member management, and NO ownership. Starting set per
  // ENG-5696 — confirm final scope (esp. financials) with product before GA.
  [ROLES.accountSupport]: [
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.resolve,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.accountStores.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.label.read,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.settingsMetadata.read,
    ENTITLEMENTS.sop.read,
  ],
  [ROLES.opGlobalSupport]: [
    ...Object.values(ENTITLEMENTS.label),
    ENTITLEMENTS.settingsMetadata.read,
    ENTITLEMENTS.billingSettings.read,
    ENTITLEMENTS.reviews.manage,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.metadata.update,
    ENTITLEMENTS.metadata.create,
    ENTITLEMENTS.metadata.read,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.support.memberRead,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.resolve,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.resolve,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.create,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.adminStores.read,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.pricing.read,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.customers.read,
  ],
  [ROLES.opSupportAdmin]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ...Object.values(ENTITLEMENTS.billingSettings),
    ...Object.values(ENTITLEMENTS.sop),
    ...Object.values(ENTITLEMENTS.settingsMetadata),
    ENTITLEMENTS.pricing.read,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.returns.update,
    ENTITLEMENTS.reviews.manage,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.claimFiling.read,
    ENTITLEMENTS.claimFiling.update,
    ENTITLEMENTS.adminStores.read,
    ENTITLEMENTS.support.memberRead,
    ENTITLEMENTS.support.memberCreate,
    ENTITLEMENTS.support.memberUpdate,
    ENTITLEMENTS.support.memberDelete,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.resolve,
    ENTITLEMENTS.claim.reset,
    ENTITLEMENTS.claim.escalationReply,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.warranties.resolve,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.message.create,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.settingsCustomerCommunication.read,
    ENTITLEMENTS.settingsFulfillment.read,
    ENTITLEMENTS.settingsReplacementCost.read,
    ENTITLEMENTS.settingsBackorderReship.read,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.read,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.settingsRevShare.read,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.teamMember.edit,
    ENTITLEMENTS.integrations.read,
    ENTITLEMENTS.integrations.update,
    ENTITLEMENTS.apiTokens.read,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.paymentMethod.read,
    ENTITLEMENTS.payoutMethod.read,
    ENTITLEMENTS.analytics.read,
    ENTITLEMENTS.invoice.read,
    ENTITLEMENTS.billing.read,
    ENTITLEMENTS.payout.read,
    ENTITLEMENTS.capabilities.read,
    ENTITLEMENTS.macro.create,
    ENTITLEMENTS.macro.update,
    ENTITLEMENTS.globalMacro.read,
    ENTITLEMENTS.globalMacro.create,
    ENTITLEMENTS.globalMacro.update,
    ENTITLEMENTS.customers.read,
  ],

  [ROLES.developer]: [
    ...Object.values(ENTITLEMENTS.developerApp),
    ENTITLEMENTS.developerInstallation.read,
    ENTITLEMENTS.marketplace.read,
    ENTITLEMENTS.marketplace.install,
    ENTITLEMENTS.marketplace.uninstall,
    ENTITLEMENTS.account.read,
    // Required to call GET /v1/stores (the list endpoint guards on account-level store:read);
    // findUserStores then scopes results to the dev's approved collaborator stores. Without this
    // a developer is 403'd and sees no stores at all — including ones they collaborate on.
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.user.read,
    // Org membership: any developer in the org can view + invite other developers.
    ENTITLEMENTS.user.create,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.teamMember.invite,
  ],

  // should only have read access to claims & orders pages
  [ROLES.opPartner]: [
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.store.allRead,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.pricing.read,
    ENTITLEMENTS.metadata.read,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.returns.read,
  ],
};

export const STORE_ROLE_ENTITLEMENTS_MAP = {
  [STORE_ROLES.storeOwner]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ...Object.values(ENTITLEMENTS.billingSettings),
    ...Object.values(ENTITLEMENTS.settingsMetadata),
    ENTITLEMENTS.billingMethod.create,
    ENTITLEMENTS.billingMethod.read,
    ENTITLEMENTS.billingMethod.update,
    ENTITLEMENTS.returns.update,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.customizations.update,
    ENTITLEMENTS.teamMember.changeRole,
    ENTITLEMENTS.teamMember.grantOwnership,
    ENTITLEMENTS.teamMember.invite,
    ENTITLEMENTS.teamMember.edit,
    ENTITLEMENTS.teamMember.remove,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.macro.create,
    ENTITLEMENTS.macro.delete,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.macro.update,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.store.update,
    ENTITLEMENTS.user.create,
    ENTITLEMENTS.user.delete,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.finance.read,
    ENTITLEMENTS.analytics.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.payout.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.invoice.read,
    ENTITLEMENTS.paymentMethod.create,
    ENTITLEMENTS.paymentMethod.delete,
    ENTITLEMENTS.paymentMethod.read,
    ENTITLEMENTS.paymentMethod.update,
    ENTITLEMENTS.payoutMethod.create,
    ENTITLEMENTS.payoutMethod.delete,
    ENTITLEMENTS.payoutMethod.read,
    ENTITLEMENTS.payoutMethod.update,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.webhook.create,
    ENTITLEMENTS.webhook.delete,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.webhook.update,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.billing.read,
    ENTITLEMENTS.settingsCustomerCommunication.read,
    ENTITLEMENTS.settingsCustomerCommunication.update,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.update,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.settingsFulfillment.read,
    ENTITLEMENTS.settingsFulfillment.update,
    ENTITLEMENTS.settingsReplacementCost.read,
    ENTITLEMENTS.settingsReplacementCost.update,
    ENTITLEMENTS.settingsBackorderReship.read,
    ENTITLEMENTS.settingsBackorderReship.update,
    ENTITLEMENTS.apiTokens.create,
    ENTITLEMENTS.apiTokens.delete,
    ENTITLEMENTS.apiTokens.read,
    ENTITLEMENTS.apiTokens.update,
    ENTITLEMENTS.integrations.read,
    ENTITLEMENTS.integrations.update,
    ENTITLEMENTS.abTests.read,
    ENTITLEMENTS.abTests.delete,
    ENTITLEMENTS.abTests.update,
    ENTITLEMENTS.abTests.create,
    ENTITLEMENTS.settingsClaimAcknowledgment.read,
    ENTITLEMENTS.settingsClaimAcknowledgment.update,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.sop.delete,
    ENTITLEMENTS.sop.update,
    ENTITLEMENTS.sop.create,
    ENTITLEMENTS.sop.setState,
    ...Object.values(ENTITLEMENTS.developerApp),
    ENTITLEMENTS.developerInstallation.read,
    ENTITLEMENTS.customers.read,
  ],
  [STORE_ROLES.storeAdmin]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ...Object.values(ENTITLEMENTS.billingSettings),
    ...Object.values(ENTITLEMENTS.settingsMetadata),
    ENTITLEMENTS.billingMethod.create,
    ENTITLEMENTS.billingMethod.read,
    ENTITLEMENTS.billingMethod.update,
    ENTITLEMENTS.returns.update,
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.protection.add,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.customizations.update,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.integrations.read,
    ENTITLEMENTS.integrations.update,
    ENTITLEMENTS.teamMember.changeRole,
    ENTITLEMENTS.teamMember.invite,
    ENTITLEMENTS.teamMember.edit,
    ENTITLEMENTS.teamMember.remove,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.macro.create,
    ENTITLEMENTS.macro.delete,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.macro.update,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.store.update,
    ENTITLEMENTS.user.create,
    ENTITLEMENTS.user.delete,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.analytics.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.payout.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.invoice.read,
    ENTITLEMENTS.finance.read,
    ENTITLEMENTS.paymentMethod.create,
    ENTITLEMENTS.paymentMethod.delete,
    ENTITLEMENTS.paymentMethod.read,
    ENTITLEMENTS.paymentMethod.update,
    ENTITLEMENTS.payoutMethod.create,
    ENTITLEMENTS.payoutMethod.delete,
    ENTITLEMENTS.payoutMethod.read,
    ENTITLEMENTS.payoutMethod.update,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.webhook.create,
    ENTITLEMENTS.webhook.delete,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.webhook.update,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.billing.read,
    ENTITLEMENTS.settingsCustomerCommunication.read,
    ENTITLEMENTS.settingsCustomerCommunication.update,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.read,
    ENTITLEMENTS.settingsOneTimeAppeasements.update,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.settingsFulfillment.read,
    ENTITLEMENTS.settingsFulfillment.update,
    ENTITLEMENTS.settingsReplacementCost.read,
    ENTITLEMENTS.settingsReplacementCost.update,
    ENTITLEMENTS.settingsBackorderReship.read,
    ENTITLEMENTS.settingsBackorderReship.update,
    ENTITLEMENTS.apiTokens.create,
    ENTITLEMENTS.apiTokens.delete,
    ENTITLEMENTS.apiTokens.read,
    ENTITLEMENTS.apiTokens.update,
    ENTITLEMENTS.abTests.read,
    ENTITLEMENTS.abTests.delete,
    ENTITLEMENTS.abTests.update,
    ENTITLEMENTS.abTests.create,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.settingsClaimAcknowledgment.read,
    ENTITLEMENTS.settingsClaimAcknowledgment.update,
    ENTITLEMENTS.sop.read,
    ENTITLEMENTS.sop.delete,
    ENTITLEMENTS.sop.update,
    ENTITLEMENTS.sop.create,
    ENTITLEMENTS.sop.setState,
    ...Object.values(ENTITLEMENTS.developerApp),
    ENTITLEMENTS.developerInstallation.read,
    ENTITLEMENTS.customers.read,
  ],
  [STORE_ROLES.storeSupport]: [
    ...Object.values(ENTITLEMENTS.metadata),
    ...Object.values(ENTITLEMENTS.label),
    ENTITLEMENTS.returns.read,
    ENTITLEMENTS.reviews.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.resolve,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.warranties.resolve,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.create,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.contact.read,
    ENTITLEMENTS.webhook.read,
  ],
  [STORE_ROLES.storeUser]: [
    ...Object.values(ENTITLEMENTS.label),
    ENTITLEMENTS.metadata.create,
    ENTITLEMENTS.protection.remove,
    ENTITLEMENTS.metadata.read,
    ENTITLEMENTS.macro.read,
    ENTITLEMENTS.store.read,
    ENTITLEMENTS.user.read,
    ENTITLEMENTS.user.update,
    ENTITLEMENTS.teamMember.read,
    ENTITLEMENTS.claim.create,
    ENTITLEMENTS.claim.update,
    ENTITLEMENTS.claim.read,
    ENTITLEMENTS.warranties.read,
    ENTITLEMENTS.warranties.update,
    ENTITLEMENTS.warranties.create,
    ENTITLEMENTS.product.create,
    ENTITLEMENTS.product.delete,
    ENTITLEMENTS.product.read,
    ENTITLEMENTS.product.update,
    ENTITLEMENTS.claimNote.create,
    ENTITLEMENTS.claimNote.delete,
    ENTITLEMENTS.claimNote.read,
    ENTITLEMENTS.claimNote.update,
    ENTITLEMENTS.message.read,
    ENTITLEMENTS.notifications.read,
    ENTITLEMENTS.account.read,
    ENTITLEMENTS.policies.read,
    ENTITLEMENTS.quotePricing.read,
    ENTITLEMENTS.webhook.read,
    ENTITLEMENTS.order.read,
    ENTITLEMENTS.order.update,
    ENTITLEMENTS.storeCredit.read,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.contact.read,
  ],
  // Cross-account collaborator (Shopify-style). Granted to a developer org's members on a
  // merchant store they've been approved to collaborate on. Deliberately minimal: customizations
  // read/write + settings read, plus marketplace install so they can install the org's apps on
  // the collab store. NOTHING else (no claims/orders/billing/products/team/etc.).
  [STORE_ROLES.storeCollaborator]: [
    ENTITLEMENTS.customizations.read,
    ENTITLEMENTS.customizations.update,
    ENTITLEMENTS.settingsGeneral.read,
    ENTITLEMENTS.settingsWidget.read,
    ENTITLEMENTS.marketplace.read,
    ENTITLEMENTS.marketplace.install,
    ENTITLEMENTS.marketplace.uninstall,
    ENTITLEMENTS.developerInstallation.read,
  ],
};
